import type { RevisionImpact } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { api } from '../../lib/api';
import { useMe } from '../../lib/session';
import { Badge, ErrorBox, Spinner } from '../../ui/feedback';
import { Panel } from '../../ui/layout';

/**
 * Что затрагивает ревизия (ПД-08): изменённые листы и связанные с ними позиции сметы, работы графика, заявки и акты
 * скрытых работ. Блоки — по правам разделов, сумм нет. Видят сотрудники с правом на ПСД.
 */
export function RevisionImpactPanel({ revisionId, projectId }: { revisionId: string; projectId: string }) {
  const me = useMe();
  const staff = me.tenant?.staff ?? false;
  const q = useQuery({
    queryKey: ['psd-impact', revisionId],
    queryFn: () => api<RevisionImpact>(`/psd/revisions/${revisionId}/impact`),
    enabled: staff,
  });
  if (!staff) return null;
  const link = 'inline-flex min-h-11 items-center text-ink underline-offset-4 hover:underline md:min-h-0';
  const d = q.data;
  // null — нет права на раздел: это не «ничего не затронуто», а «не видно»
  const hidden = d
    ? [
        d.estimateItems === null ? 'сметы' : null,
        d.activities === null ? 'график' : null,
        d.requests === null ? 'закупки' : null,
        d.inspections === null ? 'надзор' : null,
      ].filter((x): x is string => Boolean(x))
    : [];
  const empty =
    d &&
    !d.estimateItems?.length &&
    !d.activities?.length &&
    !d.requests?.length &&
    !d.inspections?.length &&
    !d.openRemarks &&
    !d.openQuestions;
  return (
    <Panel
      title="Что затрагивает ревизия"
      meta={
        d ? (d.first ? 'первый выпуск — весь комплект' : `изменено листов: ${d.sheets.length}`) : undefined
      }
    >
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : empty ? (
        <p className="text-[14px] text-ink-2">
          {hidden.length
            ? `В доступных вам разделах с изменёнными листами ничего не связано. Нет доступа: ${hidden.join(', ')}.`
            : 'С изменёнными листами не связаны ни позиции сметы, ни работы графика, ни заявки и акты.'}
        </p>
      ) : (
        <div className="grid gap-4 text-[14px]">
          {d!.estimateItems?.length ? (
            <section>
              <h3 className="mb-1 text-[13px] font-medium text-ink-2">
                Позиции сметы ({d!.estimateItems.length})
              </h3>
              <ul className="grid gap-1">
                {d!.estimateItems.slice(0, 30).map((i) => (
                  <li key={i.id}>
                    <Link to="/estimates/$estimateId" params={{ estimateId: i.estimateId }} className={link}>
                      {i.estimateCode} · п. {i.number} {i.name}
                      {i.sheetNumber ? ` (лист ${i.sheetNumber})` : ''}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          {d!.activities?.length ? (
            <section>
              <h3 className="mb-1 text-[13px] font-medium text-ink-2">
                Работы графика ({d!.activities.length})
              </h3>
              <ul className="grid gap-1">
                {d!.activities.slice(0, 30).map((a) => (
                  <li key={a.id}>
                    <Link
                      to="/schedule/$projectId"
                      params={{ projectId }}
                      search={{} as never}
                      className={link}
                    >
                      {a.code} {a.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          {d!.requests?.length ? (
            <section>
              <h3 className="mb-1 text-[13px] font-medium text-ink-2">
                Заявки на материалы ({d!.requests.length})
              </h3>
              <ul className="grid gap-1">
                {d!.requests.slice(0, 30).map((r) => (
                  <li key={r.id}>
                    <Link to="/procurement/$requestId" params={{ requestId: r.id }} className={link}>
                      {r.number} {r.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          {d!.inspections?.length ? (
            <section>
              <h3 className="mb-1 text-[13px] font-medium text-ink-2">
                Акты и вызовы скрытых работ по изменённым листам ({d!.inspections.length})
              </h3>
              <ul className="grid gap-1">
                {d!.inspections.slice(0, 30).map((i) => (
                  <li key={i.id} className="flex flex-wrap items-center gap-2">
                    <Link
                      to="/supervision/inspections/$inspectionId"
                      params={{ inspectionId: i.id }}
                      className={link}
                    >
                      {i.number} {i.workName}
                    </Link>
                    <Badge tone={i.status === 'accepted' ? 'warning' : 'neutral'}>
                      {i.status === 'accepted' ? 'акт подписан — проверить' : 'вызов ждёт решения'}
                    </Badge>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          {d!.openRemarks || d!.openQuestions ? (
            <p className="text-ink-2">
              Открыто по изменённым листам: замечаний {d!.openRemarks ?? 0}, вопросов {d!.openQuestions ?? 0}{' '}
              — их актуальность проверяется по новой ревизии.
            </p>
          ) : null}
          {hidden.length ? (
            <p className="text-[13px] text-ink-2">Нет доступа к разделам: {hidden.join(', ')}.</p>
          ) : null}
          {!d!.first ? (
            <p className="text-[13px] text-ink-2">
              При штампе «В производство работ» составители затронутых смет и графика получают поручения
              проверить свои позиции и работы.
            </p>
          ) : null}
        </div>
      )}
    </Panel>
  );
}
